import { Divider, Typography } from '@mui/material';
import { getDocumentsAvailableForConfirming } from 'lib/api/confirming';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import OperationResume from '../OperationResume';
import AddDocumentsConfirming from './components/AddDocumentsConfirming';

const DocumentSelector = (props: any) => {
  const {
    business,
    selected,
    setSelected,
    documents,
    setDocuments,
    setCachedDocuments,
    cachedDocuments,
  } = props;

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [businessName, setBusinessName] = useState<string>('');
  const [businessIdentifier, setBusinessIdentifier] = useState<string>('');
  const [folio, setFolio] = useState<string>('');
  const [enrolledType, setEnrolledType] = useState<string>('');
  const [configuration, setConfiguration] = useState<any>({});
  const [tableLoading, setTableLoading] = useState(false);
  const [openAddDocumentsConfirming, setOpenAddDocumentsConfirming] =
    useState(false);

  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (folio) query += `folio=${folio}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName)
      query += `businessName=${businessName.replace('&', '%26')}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}&`;
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}`;
    if (selected.length) query += `selectedDocuments=${selected.join(',')}`;
    if (enrolledType) query += `enrolledType=${enrolledType}`;
    return query;
  };

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const getDocumentsAvailable = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setTableLoading(true);
    const query = generateQuery({
      page,
      pageSize,
    });
    const response = await getDocumentsAvailableForConfirming(
      dispatch,
      {
        identifier: business.identifier,
        subProductTypeId: 'electronic-invoice-confirming',
      },
      query,
    );
    setDocuments(response.data);
    setCachedDocuments([
      ...cachedDocuments,
      ...response.data,
      ...response.selectedDocuments,
    ]);
    setPagination(response.pagination);
    setConfiguration(response.configuration);
    setTableLoading(false);
    return response;
  };

  const triggerSearch = async () => {
    setLoading(true);
    await getDocumentsAvailable({
      page: 0,
      pageSize: PAGE_SIZE,
    });
    setLoading(false);
  };

  useEffect(() => {
    if (business?.identifier) {
      void triggerSearch();
    }
  }, []);

  const filters: Filter[] = [
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de confirmación',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Nombre proveedor',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut proveedor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Tipo de inyección',
      type: 'select',
      options: [
        { value: 'sii', label: 'SII' },
        { value: 'manual', label: 'Creados manualmente' },
      ],
      value: enrolledType,
      setter: setEnrolledType,
    },
  ];

  const handleSetSelected = (selectedIds: any) => {
    const newCachedDocuments = [...cachedDocuments];
    for (const id of selectedIds) {
      const document = documents.find((document: any) => document.id === id);
      if (document) {
        const newCachedIds = [
          ...newCachedDocuments.map((document: any) => document.id),
        ];
        if (!newCachedIds.includes(document.id)) {
          newCachedDocuments.push(document);
        }
      }
    }
    setCachedDocuments(newCachedDocuments);
    setSelected(selectedIds);
  };

  const cantSelectDocuments = (document: any) => {
    if (configuration.dontFilterOwnInvoices) {
      if (document.cessionaryIdentifier === configuration.companyIdentifier) {
        return false;
      }
    }
    if (configuration.showOnlyAvailables) {
      if (document.status === 'Transable') {
        return false;
      }
    }
    if (
      !configuration.dontFilterOwnInvoices &&
      !configuration.showOnlyAvailables
    ) {
      return false;
    }
    return true;
  };

  if (loading) {
    return <CenteredCircularProgress type="dialog" />;
  }

  return (
    <div>
      <OperationResume
        stakeholderName={business.businessName}
        stakeholderIdentifier={business.identifier}
      />
      <Divider />
      <div style={{ padding: '20px 0px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <Typography variant="h6" gutterBottom>
              Documentos disponibles
            </Typography>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <GlobalSearcher
              filters={filters}
              triggerSearch={triggerSearch}
              dispatch={dispatch}
              disableSearch={loading || tableLoading}
            />
            <CustomButton
              variant="contained"
              color="primary"
              onClick={() => setOpenAddDocumentsConfirming(true)}
            >
              Agregar documentos
            </CustomButton>
          </div>
        </div>
        <div>
          <Typography>{selected.length} documentos seleccionados.</Typography>
        </div>
        <div>
          <TableWithPagination
            dataHeaders={[
              { key: 'folio', label: 'Folio' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'amountAvailable',
                label: 'Monto disponible',
                type: 'money',
              },
              {
                key: 'businessName',
                label: 'Proveedor',
              },
              {
                key: 'issuedDate',
                label: 'Fecha de emisión',
                type: 'eight-days-date',
              },
              {
                key: 'confirmDate',
                label: 'Fecha de confirmación',
                type: 'date',
              },
              {
                key: 'status',
                label: 'Estado',
                type: 'cession',
              },
              {
                key: 'cessionary',
                label: 'Cessionario',
              },
            ]}
            cantSelectRow={cantSelectDocuments}
            data={documents}
            selected={selected}
            setSelected={handleSetSelected}
            setData={setDocuments}
            pagination={pagination}
            setPagination={setPagination}
            loading={tableLoading}
            searchData={getDocumentsAvailable}
          />
        </div>
      </div>
      {openAddDocumentsConfirming && (
        <AddDocumentsConfirming
          open={openAddDocumentsConfirming}
          setOpen={setOpenAddDocumentsConfirming}
          stakeholderIdentifier={business.identifier}
          handleSearchSuggestedDocuments={() => void triggerSearch()}
        />
      )}
    </div>
  );
};

export default DocumentSelector;
